import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { TOKEN_KEY, formatApiError } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [status, setStatus] = useState({}); // has_family, has_child, intake_status, has_roadmap
  const [loading, setLoading] = useState(true);

  const loadMe = useCallback(async () => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      setUser(false);
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data.user);
      setSubscription(data.subscription);
      setStatus({
        has_family: data.has_family,
        has_child: data.has_child,
        intake_status: data.intake_status,
        has_roadmap: data.has_roadmap,
      });
    } catch (e) {
      localStorage.removeItem(TOKEN_KEY);
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadMe();
  }, [loadMe]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem(TOKEN_KEY, data.access_token);
    await loadMe();
    return data.user;
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    localStorage.setItem(TOKEN_KEY, data.access_token);
    await loadMe();
    return data.user;
  };

  const providerRegister = async (payload) => {
    const { data } = await api.post("/auth/provider/register", payload);
    localStorage.setItem(TOKEN_KEY, data.access_token);
    await loadMe();
    return data.user;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (e) { if (process.env.NODE_ENV === "development") console.warn("Logout request failed (clearing session anyway)", e); }
    localStorage.removeItem(TOKEN_KEY);
    setUser(false);
    setSubscription(null);
    setStatus({});
  };

  const isPaid = subscription?.plan_key === "family_path" && ["active", "trialing"].includes(subscription?.status);

  return (
    <AuthContext.Provider value={{ user, subscription, status, loading, isPaid, login, register, providerRegister, logout, refresh: loadMe }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
export { formatApiError };
